import axios from 'axios';
import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useParams } from "react-router-dom";
import { Klaviyo, Shopify, JudgeMe, MetaAds } from '../components/IntegrationsComponents';


export default function UpdateIntegration() {
  const { handleSignOut, getJwtToken, canWrite } = useContext(AuthContext);
  const {id} = useParams();
  const [integration, setIntegration] = useState(false);
  const [ret, setRet] = useState(false);
  const [message, setMessage] = useState(null);
  const [promptKlaviyoReauth, setPromptKlaviyoReauth] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const shouldPoll = searchParams.get('polling') === 'true';
    
    // Check for success/error messages from OAuth redirects
    if (searchParams.get('meta') === 'missing_scope') {
      setMessage({
        type: 'error',
        text: 'Facebook connected, but some permissions were declined. Reconnect and allow access to your ad accounts.'
      });
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    } else if (searchParams.get('integration') === 'missing_scopes') {
      setMessage({
        type: 'error',
        text: 'Klaviyo connected, but campaigns/flows permissions were not granted. Confirm reconnect and approve the updated access — also verify campaigns:read and flows:read are enabled on the Amplify Klaviyo app.'
      });
      setPromptKlaviyoReauth(true);
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    } else if (searchParams.get('integration') === 'success') {
      setMessage({ type: 'success', text: 'Integration connected successfully!' });
      // Clean up the URL
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    } else if (searchParams.get('integration') === 'error') {
      setMessage({ type: 'error', text: 'Failed to connect integration. Please try again.' });
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    } else if (searchParams.get('klaviyo') === 'error') {
      setMessage({ type: 'error', text: 'Failed to connect integration. Please try again.' });
      // Clean up the URL
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    } else if (searchParams.get('judgeme') === 'error') {
      setMessage({ type: 'error', text: 'Failed to connect Judge.me. Please try again.' });
      // Clean up the URL
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    } else if (searchParams.get('judgeme') === 'success') {
      setMessage({ type: 'success', text: 'Judge.me connected successfully!' });
      // Clean up the URL
      window.history.replaceState({}, '', `/app/integrations/${id}`);
    }

    const getIntegration = async (retries = 5) => {
      if (shouldPoll && retries === 0) {
        console.error("Failed to get integration status after multiple attempts.");
        // Optionally, set an error state here to inform the user
        return;
      }

      try {
        const token = await getJwtToken();
        const res = await axios.get('https://api.amplifyloyalty.com/app/stores/integrations',
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              integration: id
            }
          }
        );
        if (res.data.name === 'BadRequestError') {
          handleSignOut();
        } else if (shouldPoll && res.data.status === 'inactive') {
          // If status is still inactive and we should be polling, wait and retry.
          setTimeout(() => getIntegration(retries - 1), 2000);
        }
        else {
          setIntegration(res.data);
          if (res.headers['x-amplify-ret']) setRet(JSON.parse(res.headers['x-amplify-ret']));
        }
      } catch (err) {
        console.log(err);
        if (shouldPoll) {
          // Also retry on network errors if polling is active
          setTimeout(() => getIntegration(retries - 1), 2000);
        }
      }
    }
    getIntegration();
  }, [id, handleSignOut, getJwtToken]);

  // Auto-hide message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="update-integration">
      {message && (
        <div className={`integration-message integration-message--${message.type}`} style={{
          padding: '15px 20px',
          marginBottom: '20px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          textAlign: 'center',
          fontWeight: '500'
        }}>
          {message.text}
        </div>
      )}
      {!canWrite && (
        <p className="settings__sub-heading" style={{ marginBottom: '1rem', padding: '0 1rem' }}>
          You have read-only access. Integration settings cannot be changed.
        </p>
      )}
      {id === 'shopify' && <Shopify integration={integration} setIntegration={setIntegration} ret={ret} readOnly={!canWrite} />}
      {id === 'klaviyo' && (
        <Klaviyo
          integration={integration}
          setIntegration={setIntegration}
          readOnly={!canWrite}
          promptReauth={promptKlaviyoReauth}
          onReauthPrompted={() => setPromptKlaviyoReauth(false)}
        />
      )}
      {id === 'judgeme' && <JudgeMe integration={integration} setIntegration={setIntegration} readOnly={!canWrite} />}
      {id === 'meta-ads' && <MetaAds integration={integration} setIntegration={setIntegration} readOnly={!canWrite} />}
    </div>
  )
}